// Pages a visitor has opened on the store in this visit, for "show from the Nth
// page" (rules_settings.when_to_show.min_page_views, 30 Oct 2026, Carmién: "show
// the popup after they've viewed 1 page").
//
// A visit = page loads less than 30 minutes apart (the usual analytics session),
// kept in localStorage so a page opened in a new tab still counts. Only pages that
// load the popup script count, and each load counts once however many popups or
// re-renders it has.

const KEY = "flowapt_popup_visit";
const VISIT_GAP_MS = 30 * 60 * 1000;

interface VisitRecord {
  count: number;
  lastAt: number;
}

let countedThisLoad: number | null = null;

function read(storage: Storage): VisitRecord | null {
  try {
    const raw = storage.getItem(KEY);
    const v = raw ? JSON.parse(raw) : null;
    return v && Number.isFinite(v.count) && Number.isFinite(v.lastAt) ? v : null;
  } catch {
    return null;
  }
}

/** Count this page load (once) and return the visit's page number: 1 on the
 *  page someone lands on, 2 on the next one they open, … */
export function countPageView(storage: Storage | undefined = safeStorage(), now = Date.now()): number {
  if (countedThisLoad !== null) return countedThisLoad;
  if (!storage) return (countedThisLoad = 1);
  const prev = read(storage);
  const count = prev && now - prev.lastAt <= VISIT_GAP_MS ? prev.count + 1 : 1;
  try {
    storage.setItem(KEY, JSON.stringify({ count, lastAt: now }));
  } catch {
    /* storage full or blocked: this page still counts */
  }
  return (countedThisLoad = count);
}

/** This page's number in the visit (1 when it has not been counted). */
export function pageViewsThisVisit(): number {
  return countedThisLoad ?? 1;
}

function safeStorage(): Storage | undefined {
  try {
    return typeof window !== "undefined" ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

/** Tests only: forget this load's count. */
export function resetPageViewCountForTests(): void {
  countedThisLoad = null;
}
